import { createSlice, PayloadAction } from "@reduxjs/toolkit";

export type DynamicViewTab = "Grid" | "List" | "Board";

export interface DynamicViewTabItem {
  label: DynamicViewTab;
  value: string;
}

export const DynamicViewTabs: DynamicViewTabItem[] = [
  { label: "Grid", value: "Grid" },
  { label: "List", value: "List" },
  { label: "Board", value: "Board" },
];

// 1. Define the Shape of a SINGLE Route's State
interface DynamicRouteState {
  currentViewTab: DynamicViewTab;
  currentTab: string;
  isEditMode: boolean;
  selectedCourseId: string | null;
  selectedApplicationId: string | null;
  isFollowUpOverrideConfirmation: boolean;
  isAddApplication: boolean;
  isAddQualification: boolean;
  isFilterOpen: boolean;
  // Selection & bulk actions
  selectedLeadIds: (string | number)[];
  isAssignMode: boolean;
  isUpdateMode: boolean;
}

// 2. Default Values (The Template)
export const defaultRouteState: DynamicRouteState = {
  currentViewTab: "Grid",
  currentTab: "Overview",
  isEditMode: false,
  selectedCourseId: null,
  selectedApplicationId: null,
  isFollowUpOverrideConfirmation: false,
  isAddApplication: false,
  isAddQualification: false,
  isFilterOpen: false,
  selectedLeadIds: [],
  isAssignMode: false,
  isUpdateMode: false,
};

// 3. Main State is a Map: { [routeId]: RouteState }
export type DynamicFunctionalState = Record<string | number, DynamicRouteState>;
const initialState: DynamicFunctionalState = {};

const dynamicFunctionalSlice = createSlice({
  name: "dynamicFunctional",
  initialState,
  reducers: {
    // --- INITIALIZER ---
    initializeFunctionalRoute: (
      state,
      action: PayloadAction<string | number>
    ) => {
      const routeId = action.payload;
      if (!state[routeId]) {
        state[routeId] = { ...defaultRouteState };
      }
    },
    setIsFilterOpen(state, action) {
      const { routeId, isOpen } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].isFilterOpen = isOpen;
    },

    // --- SETTERS (All must accept routeId) ---
    setDynamicCurrentViewTab: (
      state,
      action: PayloadAction<{ routeId: string | number; view: DynamicViewTab }>
    ) => {
      const { routeId, view } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].currentViewTab = view;
    },

    setDynamicCurrentTab: (
      state,
      action: PayloadAction<{ routeId: string | number; tab: string }>
    ) => {
      const { routeId, tab } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].currentTab = tab;
    },

    setIsEditMode: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].isEditMode = value;
    },

    setSelectedCourseId: (
      state,
      action: PayloadAction<{ routeId: string | number; id: string | null }>
    ) => {
      const { routeId, id } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].selectedCourseId = id;
    },

    setSelectedApplicationId: (
      state,
      action: PayloadAction<{ routeId: string | number; id: string | null }>
    ) => {
      const { routeId, id } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].selectedApplicationId = id;
    },

    setIsFollowUpOverrideConfirmation: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].isFollowUpOverrideConfirmation = value;
    },

    setIsAddApplication: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].isAddApplication = value;
    },

    setIsAddQualification: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].isAddQualification = value;
    },

    // --- Selection & Bulk Actions ---
    toggleDynamicLeadSelection: (
      state,
      action: PayloadAction<{ routeId: string | number; id: string | number }>
    ) => {
      const { routeId, id } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      const current = state[routeId].selectedLeadIds;
      const ids = Array.isArray(current) ? current : [];
      if (ids.includes(id)) {
        state[routeId].selectedLeadIds = ids.filter((item) => item !== id);
      } else {
        state[routeId].selectedLeadIds = [...ids, id];
      }
    },
    selectAllDynamicLeads: (
      state,
      action: PayloadAction<{ routeId: string | number; ids: (string | number)[] }>
    ) => {
      const { routeId, ids } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].selectedLeadIds = ids;
    },
    clearDynamicSelectedLeadIds: (
      state,
      action: PayloadAction<string | number>
    ) => {
      const routeId = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].selectedLeadIds = [];
    },
    setDynamicIsAssignMode: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].isAssignMode = value;
    },
    setDynamicIsUpdateMode: (
      state,
      action: PayloadAction<{ routeId: string | number; value: boolean }>
    ) => {
      const { routeId, value } = action.payload;
      if (!state[routeId]) state[routeId] = { ...defaultRouteState };
      state[routeId].isUpdateMode = value;
    },
  },
});

export const {
  initializeFunctionalRoute,
  setDynamicCurrentViewTab,
  setDynamicCurrentTab,
  setIsEditMode,
  setSelectedCourseId,
  setSelectedApplicationId,
  setIsFollowUpOverrideConfirmation,
  setIsAddApplication,
  setIsAddQualification,
  setIsFilterOpen,
  toggleDynamicLeadSelection,
  selectAllDynamicLeads,
  clearDynamicSelectedLeadIds,
  setDynamicIsAssignMode,
  setDynamicIsUpdateMode,
} = dynamicFunctionalSlice.actions;

// --- SMART SELECTOR ---
// Usage: const { currentViewTab } = useAppSelector(state => selectDynamicRouteState(state, routeId));
// Merge with defaults so persisted/partial slices never omit `selectedLeadIds` etc. (truthy `{}` would bypass `|| default`).
export const selectDynamicRouteState = (state: any, routeId: string | number) => {
  const slice = state?.dynamicFunctional?.[routeId];
  if (!slice) return defaultRouteState;
  return { ...defaultRouteState, ...slice };
};

export default dynamicFunctionalSlice.reducer;
